// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║   🎙️ DONATAPP - INTERFACCIA VOCALE ANIMATA                               ║
// ║   File: src/components/VoiceAssistantDisplay.jsx                         ║
// ║                                                                           ║
// ║   Schermata principale per la conversazione vocale                       ║
// ║   - Mostra cuore O sfera (a scelta)                                      ║
// ║   - Stati: idle, listening, thinking, speaking                           ║
// ║   - Mostra nome voce attiva (Marco, Renzo, ecc.)                         ║
// ║   - Messaggio corrente                                                   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react';
import PulsingHeart from './PulsingHeart';
import VoiceOrb from './VoiceOrb';

// 🎨 Colori Material Design
const colors = {
  background: '#F8F9FA',
  surface: '#FFFFFF',
  textPrimary: '#1A1A1A',
  textSecondary: '#666666',
  outline: '#E0E0E0',
  primary: '#1976D2',
};

// Info voci - SENZA EMOJI
const voiceInfo = {
  maria: { name: 'Marco', relation: 'figlio', color: '#FF6B9D' },
  renzo: { name: 'Renzo', relation: 'caregiver', color: '#1976D2' },
  dottore: { name: 'Dottore', relation: 'medico', color: '#4CAF50' },
  bella: { name: 'Caterina', relation: 'assistente', color: '#FF9800' },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎙️ COMPONENTE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════

const VoiceAssistantDisplay = ({
  state = 'idle',                    // 'idle' | 'listening' | 'thinking' | 'speaking'
  voiceId = 'caterina',                 // 'marco' | 'renzo' | 'dottore' | 'caterina'
  message = '',                      // Messaggio corrente
  userMessage = '',                  // Ultimo messaggio utente
  visualStyle = 'heart',             // 'heart' | 'orb'
  onTapToSpeak = null,               // Callback quando tocca per parlare
  audioLevel = 0,                    // 0-1 per reattività audio
  showHistory = false,               // Mostra storico messaggi
  conversationHistory = [],          // Array messaggi precedenti
}) => {
  const voice = voiceInfo[voiceId] || voiceInfo.bella;
  
  // Stato per animazione tap
  const [tapped, setTapped] = useState(false);
  
  // Gestisce il tap sul cuore - funziona sia per iniziare che per fermare
  const handleTap = () => {
    // Permetti tap in idle (per iniziare) o in listening (per fermare)
    if (onTapToSpeak && (state === 'idle' || state === 'listening')) {
      setTapped(true);
      setTimeout(() => setTapped(false), 200);
      onTapToSpeak();
    }
  };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: colors.background,
      padding: 20,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      
      {/* CSS per animazioni extra */}
      <style>{`
        @keyframes tap-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.95); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes typing-dots {
          0%, 20% { opacity: 0.3; }
          50% { opacity: 1; }
          80%, 100% { opacity: 0.3; }
        }
      `}</style>
      
      {/* Header con voce attiva - SENZA EMOJI */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
        padding: '12px 24px',
        background: colors.surface,
        borderRadius: 30,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: voice.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 600,
          fontSize: 16,
        }}>
          {voice.name.charAt(0)}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: colors.textPrimary }}>
            {voice.name}
          </div>
          <div style={{ fontSize: 12, color: colors.textSecondary }}>
            {voice.relation}
          </div>
        </div>
        <div style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: state === 'idle' ? '#4CAF50' : voice.color,
          marginLeft: 8,
          animation: state !== 'idle' ? 'pulse 1s ease-in-out infinite' : 'none',
        }} />
      </div>
      
      {/* Area animazione principale */}
      <div 
        onClick={handleTap}
        style={{
          cursor: (state === 'idle' || state === 'listening') && onTapToSpeak ? 'pointer' : 'default',
          animation: tapped ? 'tap-scale 0.2s ease-out' : 'none',
          transition: 'transform 0.2s',
        }}
      >
        {visualStyle === 'heart' ? (
          <PulsingHeart 
            state={state}
            voiceName={voice.name}
            size={140}
            showLabel={false}
          />
        ) : (
          <VoiceOrb 
            state={state}
            voiceName={voice.name}
            size={220}
            showLabel={false}
            audioLevel={audioLevel}
          />
        )}
      </div>
      
      {/* Stato testuale - SENZA EMOJI */}
      <div style={{
        marginTop: 16,
        fontSize: 18,
        fontWeight: 500,
        color: state === 'idle' ? colors.textSecondary : voice.color,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        {state === 'idle' && 'Tocca per parlare'}
        {state === 'listening' && (
          <>
            Ti ascolto
            <span style={{ display: 'flex', gap: 2 }}>
              {[0, 1, 2].map(i => (
                <span 
                  key={i}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: voice.color,
                    animation: `typing-dots 1s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </span>
          </>
        )}
        {state === 'thinking' && 'Sto pensando...'}
        {state === 'speaking' && `${voice.name} sta parlando`}
      </div>
      
      {/* Box messaggio utente - SENZA EMOJI */}
      {userMessage && state !== 'idle' && (
        <div style={{
          marginTop: 24,
          padding: 16,
          background: '#E3F2FD',
          borderRadius: 16,
          maxWidth: 320,
          animation: 'fade-in 0.3s ease-out',
        }}>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
            Donata ha detto:
          </div>
          <div style={{ fontSize: 16, color: colors.textPrimary }}>
            "{userMessage}"
          </div>
        </div>
      )}
      
      {/* Box risposta AI - SENZA EMOJI */}
      {message && state === 'speaking' && (
        <div style={{
          marginTop: 16,
          padding: 16,
          background: colors.surface,
          borderRadius: 16,
          maxWidth: 320,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderLeft: `4px solid ${voice.color}`,
          animation: 'fade-in 0.3s ease-out',
        }}>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
            {voice.name}:
          </div>
          <div style={{ fontSize: 16, color: colors.textPrimary, lineHeight: 1.5 }}>
            "{message}"
          </div>
        </div>
      )}
      
      {/* Storico conversazione (opzionale) - SENZA EMOJI */}
      {showHistory && conversationHistory.length > 0 && (
        <div style={{
          marginTop: 32,
          width: '100%',
          maxWidth: 400,
          maxHeight: 200,
          overflowY: 'auto',
          padding: 16,
          background: colors.surface,
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}>
          <div style={{ 
            fontSize: 12, 
            color: colors.textSecondary, 
            marginBottom: 12,
            fontWeight: 600,
          }}>
            Conversazione
          </div>
          {conversationHistory.slice(-5).map((msg, i) => (
            <div key={i} style={{
              padding: '8px 0',
              borderBottom: i < conversationHistory.length - 1 ? `1px solid ${colors.outline}` : 'none',
              fontSize: 14,
              color: msg.role === 'user' ? colors.textPrimary : colors.textSecondary,
            }}>
              <strong>{msg.role === 'user' ? 'Donata:' : `${voice.name}:`}</strong> {msg.text}
            </div>
          ))}
        </div>
      )}
      
      {/* Footer suggerimenti */}
      {state === 'idle' && (
        <div style={{
          marginTop: 40,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {['Che ore sono?', 'Ho fame', 'Dov\'è Marco?'].map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => onTapToSpeak && onTapToSpeak(suggestion)}
              style={{
                padding: '10px 16px',
                borderRadius: 20,
                border: `1px solid ${colors.outline}`,
                background: colors.surface,
                fontSize: 14,
                color: colors.textSecondary,
                cursor: 'pointer',
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// 🎮 VOICE ASSISTANT DEMO - CON VOCI REALI
// ═══════════════════════════════════════════════════════════════════════════
// Questa versione usa:
// - Microfono reale per registrare l'audio
// - OpenAI Whisper per trascrivere
// - GPT-4 + RAG per generare risposte basate sui ricordi
// - ElevenLabs per le voci reali (Marco, Renzo, Dottore, Caterina)
// ═══════════════════════════════════════════════════════════════════════════

export const VoiceAssistantDemo = () => {
  const [state, setState] = useState('idle');
  const [voice, setVoice] = useState('bella');  // voce default: Caterina
  const [style, setStyle] = useState('heart');
  const [message, setMessage] = useState('');
  const [userMsg, setUserMsg] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  
  // Refs per la registrazione audio
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const recordingTimeoutRef = React.useRef(null);

  // URL del backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.semanticai.it';

  // ─────────────────────────────────────────────────────────────────────────
  // FUNZIONE: Avvia registrazione audio dal microfono
  // ─────────────────────────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      setState('listening');
      setUserMsg('');
      setMessage('');

      // Richiedi accesso al microfono
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      // Quando arrivano dati audio, salvali
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      // Quando la registrazione finisce, processa l'audio
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        // Chiudi lo stream del microfono
        stream.getTracks().forEach(track => track.stop());
      };

      // Avvia la registrazione
      mediaRecorderRef.current.start();
      setIsRecording(true);

      // Auto-stop dopo 10 secondi
      recordingTimeoutRef.current = setTimeout(stopRecording, 10000);

    } catch (error) {
      console.error('Errore accesso microfono:', error);
      alert('Impossibile accedere al microfono. Verifica i permessi del browser.');
      setState('idle');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // FUNZIONE: Ferma la registrazione audio
  // ─────────────────────────────────────────────────────────────────────────
  const stopRecording = () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
    }

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // FUNZIONE: Processa l'audio (trascrizione + risposta AI + voce)
  // ─────────────────────────────────────────────────────────────────────────
  const processAudio = async (audioBlob) => {
    setState('thinking');

    try {
      // STEP 1: Trascrivi l'audio con Whisper
      console.log('Step 1: Trascrizione audio...');
      const transcribeFormData = new FormData();
      transcribeFormData.append('audio', audioBlob, 'recording.webm');

      const transcribeRes = await fetch(`${API_BASE_URL}/api/voice/transcribe`, {
        method: 'POST',
        headers: { 'x-api-key': 'DonatApp2025!' },
        body: transcribeFormData
      });

      if (!transcribeRes.ok) {
        throw new Error('Errore trascrizione');
      }

      const transcribeData = await transcribeRes.json();
      const userMessage = transcribeData.text;
      console.log('Trascrizione:', userMessage);
      setUserMsg(userMessage);

      // STEP 2: Genera risposta AI con RAG semantico
      console.log('Step 2: Generazione risposta AI...');
      const chatRes = await fetch(`${API_BASE_URL}/api/voice/chat-multivoce`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'DonatApp2025!'
        },
        body: JSON.stringify({
          message: userMessage,
          userId: 1
        })
      });

      if (!chatRes.ok) {
        throw new Error('Errore generazione risposta');
      }

      const chatData = await chatRes.json();
      console.log('Risposta AI:', chatData.response);
      const aiResponse = chatData.response || 'Mi fa piacere sentirti!';
      setMessage(aiResponse);

      // STEP 3: Sintetizza e riproduci audio con SpeechSynthesis del browser
      setState('speaking');

      // Usa Web Speech API per TTS (temporaneo - in futuro useremo ElevenLabs)
      const utterance = new SpeechSynthesisUtterance(aiResponse);
      utterance.lang = 'it-IT';
      utterance.rate = 0.9;
      utterance.pitch = 1.0;

      // Cerca voce italiana
      const voices = window.speechSynthesis.getVoices();
      const italianVoice = voices.find(v => v.lang.startsWith('it'));
      if (italianVoice) {
        utterance.voice = italianVoice;
      }

      utterance.onend = () => {
        setTimeout(() => {
          setState('idle');
        }, 1000);
      };

      utterance.onerror = () => {
        console.error('Errore sintesi vocale');
        setTimeout(() => {
          setState('idle');
        }, 1000);
      };

      window.speechSynthesis.speak(utterance);

    } catch (error) {
      console.error('Errore:', error);
      setUserMsg('Errore nella comunicazione');
      setMessage('Mi dispiace, non ho capito. Prova a ripetere.');
      setState('speaking');
      setTimeout(() => setState('idle'), 3000);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // FUNZIONE: Gestisci il tap sul cuore/sfera
  // ─────────────────────────────────────────────────────────────────────────
  const handleTapToSpeak = () => {
    if (state === 'idle') {
      startRecording();
    } else if (state === 'listening' && isRecording) {
      stopRecording();
    }
  };

  // Audio level animato durante speaking
  useEffect(() => {
    if (state === 'speaking') {
      const interval = setInterval(() => {
        setAudioLevel(0.3 + Math.random() * 0.7);
      }, 100);
      return () => clearInterval(interval);
    } else {
      setAudioLevel(0);
    }
  }, [state]);

  // Nomi voci in italiano per la UI
  const voiceNames = {
    maria: 'Marco',
    renzo: 'Renzo', 
    dottore: 'Dottore',
    bella: 'Caterina'
  };

  return (
    <div>
      <VoiceAssistantDisplay
        state={state}
        voiceId={voice}
        message={message}
        userMessage={userMsg}
        visualStyle={style}
        onTapToSpeak={handleTapToSpeak}
        audioLevel={audioLevel}
      />
      
      {/* Controlli demo (in basso) - SENZA EMOJI */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: 16,
        background: 'white',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        {/* Stile visivo - senza emoji */}
        <button
          onClick={() => setStyle(s => s === 'heart' ? 'orb' : 'heart')}
          style={{
            padding: '10px 20px',
            borderRadius: 20,
            border: '1px solid #E0E0E0',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {style === 'heart' ? 'Cuore' : 'Sfera'}
        </button>
        
        {/* Selezione voce - solo nomi, senza emoji */}
        {Object.keys(voiceNames).map((v) => (
          <button
            key={v}
            onClick={() => setVoice(v)}
            style={{
              padding: '10px 20px',
              borderRadius: 20,
              border: voice === v ? '2px solid #E53935' : '1px solid #E0E0E0',
              background: voice === v ? '#FFEBEE' : '#fff',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: voice === v ? 600 : 400,
              color: voice === v ? '#E53935' : '#333',
            }}
          >
            {voiceNames[v]}
          </button>
        ))}
        
        {/* Reset - senza emoji */}
        <button
          onClick={() => { setState('idle'); setMessage(''); setUserMsg(''); }}
          style={{
            padding: '10px 20px',
            borderRadius: 20,
            border: '1px solid #E0E0E0',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default VoiceAssistantDisplay;
